// eslint-disable-next-line no-unused-vars
import React, { useEffect, useState } from 'react';
import truncate from 'lodash/truncate';
import download from 'downloadjs';
import { useTranslation } from 'react-i18next';

import useAccount from 'Hooks/useAccount';
import useUser from 'Hooks/useUser';
import usePermissions from 'Hooks/usePermissions';
import useFetch from 'Hooks/useFetch';
import handleDownload from 'Utils/download';
import {
  CURRENCY_RON,
  API_DASHBOARD,
  API_BATTERY_TARIFF,
  BATTERY_TARIFF,
  API_IQUOTE,
  API_SMIS,
  API_APPLE
} from 'Constants';
import useAsyncError from 'Hooks/useAsyncError';
import useAnalytics from 'Hooks/useAnalytics';
import DashboardComponent from 'Components/Dashboard/Dashboard';

const Dashboard = () => {
  const [notifications, setNotifications] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [badges, setBadges] = useState();
  const [slides, setSlides] = useState([]);
  const [brandSlides, setBrandsSlides] = useState([]);
  const [loadingSMIS, setLoadingSMIS] = useState(false);
  const [IQuote, setIQuote] = useState('');
  const [loadingHPE, setLoadingHPE] = useState(false);
  const [loadingApple, setLoadingApple] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState({});
  const [modalOpen, setModalOpen] = useState(false);

  const { credit, creditLoading } = useAccount();
  const { data: user } = useUser();
  const fetcher = useFetch();
  const { t } = useTranslation();
  const throwError = useAsyncError();
  const analytics = useAnalytics();

  const { isExternalReseller } = usePermissions();
  const defaultCurrency = isExternalReseller ? user?.defaultCurrency : CURRENCY_RON;

  const truncateMessages = message => {
    if (message.content.length >= 300) {
      const shortContent = truncate(message.content, {
        length: 300,
        omission: '...'
      });

      return {
        ...message,
        shortContent
      };
    }

    return message;
  };

  useEffect(() => {
    if (!user?.loggedIn) return;

    const notificationsRequest = async () => {
      setDashboardLoading(true);

      try {
        const [
          {
            stats: {
              activePromotions,
              stockClearings,
              openedOrders,
              overduePayments,
              resealedProducts,
              issuedInvoices,
              messages,
              visuals,
              banners,
            }
          },
          batteryTariffData
        ] = await Promise.all([
          fetcher(API_DASHBOARD),
          fetcher(API_BATTERY_TARIFF)
        ]);

        setBadges({
          activePromotions,
          stockClearings,
          openedOrders,
          overduePayments,
          resealedProducts,
          issuedInvoices,
        });

        const hardcodedMessage = {
          messageId: 0,
          startDate: batteryTariffData?.batteryTariffUpdatedAt,
          endDate: '2099-12-31',
          title: t('user.regulationsTitle'),
          content: t('user.regulationsContent'),
          filename: BATTERY_TARIFF,
        };

        setNotifications([
          truncateMessages(hardcodedMessage),
          ...messages.map(truncateMessages)
        ]);

        setBrandsSlides(banners);
        setSlides(visuals);
      } catch (err) {
        throwError(err);
      } finally {
        setDashboardLoading(false);
      }
    };

    notificationsRequest();
  }, [user?.loggedIn]);

  useEffect(() => {
    if (IQuote) {
      document.body.style.overflowY = 'hidden';
    } else {
      document.body.style.overflowY = 'auto';
    }
  }, [IQuote]);

  const handleSMIS = async () => {
    analytics.track('smis_platform_access');
    setLoadingSMIS(true);
    const { url } = await fetcher(API_SMIS, { method: 'POST' });
    setLoadingSMIS(false);
    window.open(url, '_blank');
  };

  const handleHPE = async () => {
    analytics.track('hpe_platform_access');
    setLoadingHPE(true);
    const response = await fetcher(`${API_IQUOTE}?ref=${window.location}`);
    setLoadingHPE(false);
    setIQuote(response);
  };

  const handleApple = async () => {
    analytics.track('apple_platform_access');
    setLoadingApple(true);
    const { url } = await fetcher(API_APPLE, { method: 'POST' });
    setLoadingApple(false);
    window.open(url, '_blank');
  };

  const handleReadMore = notificationId => {
    const selected = notifications.find(n => n.messageId === notificationId);
    setSelectedNotification(selected);
    setModalOpen(true);
  };

  const handleFileDownload = async fileId => {
    const response = await fetch(`/messages/${fileId}/download`);
    const file = await response.blob();
    const blob = new Blob([file], { type: response.headers.get('content-type') });
    if (typeof document !== 'undefined') {
      const fileName = response.headers.get('content-disposition').match(/filename="(.*)"/);
      download(blob, fileName[1]);
    }
  };

  return (
    <DashboardComponent
      credit={ credit }
      creditLoading={ creditLoading }
      isExternalReseller={ isExternalReseller }
      defaultCurrency={ defaultCurrency }
      notifications={ notifications || [] }
      dashboardLoading={ dashboardLoading }
      badges={ badges }
      handleSMIS={ handleSMIS }
      loadingSMIS={ loadingSMIS }
      handleHPE={ handleHPE }
      loadingHPE={ loadingHPE }
      handleApple={ handleApple }
      loadingApple={ loadingApple }
      IQuote={ IQuote }
      handleIQuoteBackButton={ () => setIQuote() }
      handleFileDownload={ handleFileDownload }
      handleDownload={ handleDownload }
      modalOpen={ modalOpen }
      handleReadMore={ handleReadMore }
      onModalClose={ () => setModalOpen(false) }
      selectedNotification={ selectedNotification }
      slides={ slides }
      brandSlides={ brandSlides }
      isTotpEnabled={ user?.isTotpEnabled }
      isImpersonated={ user?.isImpersonated }
    />
  );
};

Dashboard.displayName = 'Dashboard';

export default Dashboard;
